import { cn } from "utils/src/cn";
import { ospThemes } from "@ospweb/sdk-ui";
import { useMemo } from "react";
import { useSnapshot } from "valtio";
import {
  isStringNull,
  isStringZero,
} from "../../../table/dashboard/columns/formatDisplay";

import NextImage from "../../../NextImage";
import { PriceColumn } from "../../../table/dashboard/columns/items/Price";
import { CRYPTO_THEME_LABELS } from "@componentsv1/radar/consts/themes";
import CheckBox from "@components/CheckBox";
import { PortfolioManage } from "@components/PortfolioManage";
import { PositionAssetTypeEnum } from "@features/api/memberApi";
import { favoritesCenter } from "@store/favorite";
import { menuSearchSelection } from "@store/menuSearchSelection";
import { ThemeItem } from "@features/api/openData";

export interface HotItemProps {
  id?: string;
  name?: string;
  symbol?: string;
  image?: string;
  price?: number;
  change?: string;
  themeValue?: ThemeItem | string;
  changClassname?: string;
  type?: "crypto" | "stock";
  onDetail?: (record: any) => void;
  isLoading?: boolean;
  onThemeClick?: (theme: ThemeItem | string) => void;
  originType?: "normal" | "portfolio";
}
export const HotItem = (props: HotItemProps) => {
  const {
    name,
    symbol,
    image,
    price,
    change,
    themeValue,
    changClassname,
    type,
    onDetail,
    isLoading,
    onThemeClick,
    originType,
    id,
  } = props;

  const showZero = isStringZero(change);
  const priceChange = Number(change || "0");
  const finalShowZero = showZero || Math.abs(priceChange) < 0.01;
  const isString = isStringNull(change);
  const formattedValue = isString
    ? "--"
    : `${priceChange > 0 ? "+" : ""}${((Math.floor(Math.abs(priceChange) * 100) / 100) * Math.sign(priceChange)).toFixed(2)}%`;

  const { updateIds, selectIds } = useSnapshot(menuSearchSelection);
  const { allFavoriteData } = useSnapshot(favoritesCenter.state);
  const checked = useMemo(() => {
    if (originType === "portfolio") {
      return allFavoriteData?.data?.[id || ""]?.favoriteStates?.length > 0;
    }
    return false;
  }, [id, originType, allFavoriteData?.data]);
  const assetType =
    type === "crypto"
      ? PositionAssetTypeEnum.TOKEN
      : PositionAssetTypeEnum.STOCK;

  const ChangeTag = () => {
    if (isString) {
      return <div className="body_medium_s text-content_primary">--</div>;
    }
    return (
      <div
        className={cn("body_medium_s", {
          "text-content_accent2_primary": priceChange < 0,
          "text-content_accent1_primary": priceChange >= 0,
          "text-content_secondary": finalShowZero,
        })}
      >
        {finalShowZero ? "0.0%" : formattedValue}
      </div>
    );
  };

  const handleCheck = () => {
    if (originType === "portfolio") {
      if (id && !checked) {
        updateIds({
          asset_id: id,
          asset_type: assetType,
        });
      }
    } else {
      onDetail?.(props);
    }
  };

  return (
    <div
      data-openreplay-label="home_hotitem_click"
      className={cn(
        "flex items-center justify-between p-4 bg-background_white_overlay5 rounded-lg cursor-pointer hover:bg-background_white_overlay10",
        {
          "opacity-50 cursor-progress": isLoading,
        },
      )}
      onClick={handleCheck}
    >
      <div className="flex items-center gap-3">
        {originType === "portfolio" ? (
          <CheckBox
            checked={
              selectIds.some(
                (item) => item.asset_id === id && item.asset_type === assetType,
              ) || checked
            }
            disabled={checked}
            iconSize={12}
          />
        ) : (
          <PortfolioManage
            id={id || ""}
            type={type === "crypto" ? "TOKEN" : "STOCK"}
            zIndex={999}
          />
        )}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full overflow-hidden border border-border_solid">
            <NextImage src={image} alt={name} width={32} height={32} />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <div className="body_medium_m text-content_primary">{symbol}</div>
              {themeValue && (
                <div
                  data-openreplay-label="home_recommendareahotitem_click"
                  onClick={(e) => {
                    e.stopPropagation();
                    onThemeClick?.(themeValue);
                  }}
                  className="px-1 py-0.5 rounded-1 border border-border_white_transparent20 body_bold_xs text-content_secondary"
                >
                  {typeof themeValue === "string"
                    ? themeValue
                    : ((themeValue as ThemeItem)?.theme as string)}
                </div>
              )}
            </div>
            <div
              className="truncate max-w-[180px] body_s text-content_secondary"
              title={name}
            >
              {name}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <PriceColumn
          record={{
            trade: {
              price_usdt: price?.toString(),
              price_change_24h: change,
            },
          }}
          index={0}
          panelType={CRYPTO_THEME_LABELS.blueChip}
          sortBy="vol_24h"
          fontColor={ospThemes.colors.content_primary}
        />
        {type === "stock" ? (
          <div className={cn("body_medium_s", changClassname)}>{change}</div>
        ) : (
          <ChangeTag />
        )}
      </div>
    </div>
  );
};
